/**
 * DIEUKYDIEU.TV - MASTER BANNER AUTO-SLIDER MODULE (js/modules/banner-slider.js)
 * Auto-rotating hero banner slider with pause-on-hover, touch gestures,
 * dot indicators, dual-mode (HTML & Image) rendering, and responsive transitions (< 200 lines).
 */

import { bannerApi } from '../api/banner.api.js';
import { t } from '../i18n.js';

export const MASTER_BANNERS = [
    {
        href: 'https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-co-nhan-loai.html',
        themeClass: 'banner-orange',
        subtitleKey: 'master_banner_author',
        subtitle: 'Nhà sáng lập Pháp Luân Đại Pháp công bố bài viết:',
        titleKey: 'banner_title_1',
        titleHtml: 'VÌ SAO CÓ NHÂN LOẠI'
    },
    {
        href: 'https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-tai-sao-can-phai-cuu-do-chung-sinh.html',
        themeClass: 'banner-purple',
        subtitleKey: 'master_banner_author',
        subtitle: 'Nhà sáng lập Pháp Luân Đại Pháp công bố bài viết:',
        titleKey: 'banner_title_2',
        titleHtml: 'TẠI SAO CẦN PHẢI<br>CỨU ĐỘ CHÚNG SINH'
    },
    {
        href: 'https://mb.dkn.tv/bai-viet-cua-dai-su-ly-hong-chi/nha-sang-lap-phap-luan-cong-cong-bo-bai-viet-vi-sao-xa-hoi-nhan-loai-la-me.html',
        themeClass: 'banner-blue',
        subtitleKey: 'master_banner_author',
        subtitle: 'Nhà sáng lập Pháp Luân Đại Pháp công bố bài viết:',
        titleKey: 'banner_title_3',
        titleHtml: 'VÌ SAO XÃ HỘI<br>NHÂN LOẠI LÀ MÊ?'
    }
];

let activeBanners = [...MASTER_BANNERS];
let currentBannerIdx = 0;
let bannerTimer = null;
let isPaused = false;
let touchStartX = 0;
let touchEndX = 0;
const AUTO_ROTATE_DELAY = 4500; // 4.5s
let eventsInitialized = false;

function buildBannerHtml(b, idx) {
    const isImage = b.type === 'image' || (!b.titleHtml && b.imageUrl);
    const href = b.href || '#';
    const target = href.startsWith('http') ? '_blank' : '_self';
    const rel = href.startsWith('http') ? 'rel="noopener noreferrer"' : '';

    if (isImage && (b.imageUrl || b.image_url)) {
        const imgUrl = b.imageUrl || b.image_url;
        const altText = b.title || 'Banner';
        return `
            <a href="${href}" target="${target}" ${rel} class="truth-banner truth-banner-image" data-banner-idx="${idx}" tabindex="0">
                <img src="${imgUrl}" alt="${altText}" class="truth-banner-img-element" loading="lazy" 
                     onerror="this.onerror=null;this.classList.add('is-broken');this.parentNode.classList.remove('truth-banner-image');this.parentNode.classList.add('banner-orange');this.parentNode.innerHTML='<div class=\\'truth-banner-content\\'><h2>${altText}</h2></div>';" />
            </a>
        `;
    }

    const themeClass = b.themeClass || (b.color ? `banner-${b.color}` : 'banner-orange');
    const subtitle = b.subtitle || (b.subtitleKey ? t(b.subtitleKey) : '');
    const title = b.titleHtml || (b.titleKey ? t(b.titleKey) : b.title || '');

    return `
        <a href="${href}" target="${target}" ${rel} class="truth-banner ${themeClass}" data-banner-idx="${idx}" tabindex="0">
            <div class="truth-banner-content">
                ${subtitle ? `<h4 ${b.subtitleKey ? `data-i18n="${b.subtitleKey}"` : ''}>${subtitle}</h4>` : ''}
                <h2 ${b.titleKey ? `data-i18n="${b.titleKey}"` : ''}>${title}</h2>
            </div>
        </a>
    `;
}

function renderBannerElements() {
    const track = document.getElementById('banner-track');
    const dotsContainer = document.getElementById('master-banner-dots');
    if (!track || !Array.isArray(activeBanners) || activeBanners.length === 0) return;

    track.innerHTML = activeBanners.map((b, idx) => buildBannerHtml(b, idx)).join('');

    if (dotsContainer) {
        dotsContainer.innerHTML = activeBanners.map((_, idx) => `
            <button class="master-banner-dot${idx === currentBannerIdx ? ' active' : ''}" 
                onclick="window.goToMasterBanner ? window.goToMasterBanner(${idx}) : goToMasterBanner(${idx})" 
                aria-label="Chuyển đến banner ${idx + 1}"
                role="tab"
                aria-selected="${idx === currentBannerIdx}"></button>
        `).join('');
    }

    goToMasterBanner(currentBannerIdx, false);
}

export async function renderMasterBanners() {
    renderBannerElements();
    initBannerSliderEvents();
    startBannerAutoPlay();

    try {
        const liveBanners = await bannerApi.getBanners();
        if (Array.isArray(liveBanners)) {
            const section = document.getElementById('master-banner-section');
            if (liveBanners.length === 0) {
                if (section) section.style.display = 'none';
                stopBannerAutoPlay();
            } else {
                if (section) section.style.display = '';
                activeBanners = liveBanners;
                currentBannerIdx = 0;
                renderBannerElements();
                startBannerAutoPlay();
            }
        }
    } catch (err) {
        console.warn('[BannerSlider] Using fallback banners due to:', err);
    }
}

export function goToMasterBanner(idx, smooth = true) {
    const track = document.getElementById('banner-track');
    const dots = document.querySelectorAll('.master-banner-dot');
    if (!track || !Array.isArray(activeBanners) || activeBanners.length === 0) return;

    currentBannerIdx = (idx + activeBanners.length) % activeBanners.length;

    track.style.transition = smooth ? 'transform 0.55s cubic-bezier(0.16, 1, 0.3, 1)' : 'none';
    track.style.transform = `translateX(-${currentBannerIdx * 100}%)`;

    dots.forEach((dot, i) => {
        const isActive = i === currentBannerIdx;
        dot.classList.toggle('active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
}

if (typeof window !== 'undefined') {
    window.goToMasterBanner = goToMasterBanner;
}

export function nextMasterBanner(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    goToMasterBanner(currentBannerIdx + 1);
}

export function prevMasterBanner(e) {
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    goToMasterBanner(currentBannerIdx - 1);
}

export function startBannerAutoPlay() {
    stopBannerAutoPlay();
    if (activeBanners.length <= 1) return;
    bannerTimer = setInterval(() => {
        if (!isPaused) {
            nextMasterBanner();
        }
    }, AUTO_ROTATE_DELAY);
}

export function stopBannerAutoPlay() {
    if (bannerTimer) {
        clearInterval(bannerTimer);
        bannerTimer = null;
    }
}

function handleSwipe() {
    const swipeDistance = touchEndX - touchStartX;
    const minSwipeDistance = 40;
    if (swipeDistance > minSwipeDistance) {
        prevMasterBanner();
    } else if (swipeDistance < -minSwipeDistance) {
        nextMasterBanner();
    }
}

function initBannerSliderEvents() {
    if (eventsInitialized) return;
    const container = document.getElementById('master-banner-container');
    if (!container) return;
    eventsInitialized = true;

    container.addEventListener('mouseenter', () => { isPaused = true; });
    container.addEventListener('mouseleave', () => { isPaused = false; });

    container.addEventListener('touchstart', (e) => {
        if (e.changedTouches && e.changedTouches[0]) {
            touchStartX = e.changedTouches[0].clientX;
            isPaused = true;
        }
    }, { passive: true });

    container.addEventListener('touchend', (e) => {
        if (e.changedTouches && e.changedTouches[0]) {
            touchEndX = e.changedTouches[0].clientX;
            isPaused = false;
            handleSwipe();
        }
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
        isPaused = document.hidden;
    });
}
